import { Box, Paper, Typography, useTheme } from "@mui/material";
import React from "react";
import type { ResearchAnswer } from "models/ResearchFindings/ResearchFindings";
import { statisticPercent } from "./researchFindingsUtils";

type Props = {
    answer: ResearchAnswer;
};

/** KBo's answer: finding, headline statistics, recommended actions, and representative quotes. */
const AnswerSection: React.FC<Props> = ({ answer }) => {
    const theme = useTheme();

    // Only meaningful, non-zero tiles, highest first, capped so the row stays readable.
    const tiles = answer.statistics
        .filter((s) => s.denominator > 0 && s.label)
        .sort(
            (a, b) =>
                statisticPercent(b.count, b.denominator) -
                statisticPercent(a.count, a.denominator),
        )
        .slice(0, 3);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography
                sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: theme.palette.gray[600],
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 1,
                }}
            >
                KBo's answer
            </Typography>

            {answer.finding && (
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: theme.palette.gray[700],
                        mb: 1.5,
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {answer.finding}
                </Typography>
            )}

            {tiles.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        mb: 1.5,
                    }}
                >
                    {tiles.map((s) => (
                        <Paper
                            key={s.label}
                            elevation={0}
                            sx={{
                                flex: "1 1 140px",
                                minWidth: "140px",
                                padding: theme.spacing(1.5),
                                borderRadius: "8px",
                                border: `1px solid ${theme.palette.gray[200]}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "22px",
                                    fontWeight: 700,
                                    color: theme.palette.gray[700],
                                }}
                            >
                                {statisticPercent(s.count, s.denominator)}%
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: theme.palette.gray[500],
                                }}
                            >
                                {s.label}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            )}

            {answer.recommendedActions.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: theme.palette.gray[700],
                            mb: 0.5,
                        }}
                    >
                        Recommended actions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {answer.recommendedActions.map((action, i) => (
                            <Typography
                                key={i}
                                component="li"
                                sx={{
                                    fontSize: "13px",
                                    color: theme.palette.gray[700],
                                    mb: 0.25,
                                }}
                            >
                                {action}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {answer.quotes.length > 0 && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: theme.palette.gray[600],
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            mb: 0.5,
                        }}
                    >
                        Representative quotes
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {answer.quotes.slice(0, 5).map((quote, i) => (
                            <Box
                                key={i}
                                sx={{
                                    borderLeft: `3px solid ${theme.palette.gray[300]}`,
                                    pl: 1.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontStyle: "italic",
                                        color: theme.palette.gray[700],
                                    }}
                                >
                                    &ldquo;{quote.text}&rdquo;
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AnswerSection;
